import {
  AfterViewInit,
  Component,
  computed,
  ContentChildren,
  ElementRef,
  input,
  InputSignal,
  OnChanges,
  OnDestroy,
  output,
  OutputEmitterRef,
  QueryList,
  signal,
  Signal,
  TemplateRef,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  combineLatest,
  fromEvent,
  map,
  Observable,
  Subscription,
  tap,
} from 'rxjs';
import { AppendTestIdPipe } from '../../../pipes/append-test-id/append-test-id.pipe';
import { DropDownComponent } from '../../data-input/form-inputs/drop-down/drop-down.component';
import { TableHeaderComponent } from './table-header/table-header.component';
import { LoadingAnimationComponent } from '../../feedback/loading-animation/loading-animation.component';
import { TableCellComponent } from './table-cell/table-cell.component';

export enum ESortOrder {
  ASC = 1,
  DESC = -1,
}
export type TableDensity = 'compact' | 'comfortable';
export type TableRowFlow = 'nowrap' | 'wrap';
export type TableTone = 'default' | 'subtle';

/*
 * table sort order can be undefined
 * when sort order is undefined, the table will not sort, but load the original order of items
 * but when actually sorting we have to compensate for the type technically being able to be undefined
 * */
export const toSortOrder = (order: TableSortOrder): ESortOrder => {
  return order === ESortOrder.ASC ? ESortOrder.ASC : ESortOrder.DESC;
};

export type TableSortOrder = ESortOrder | undefined;
export type TableExpansionMode = 'single' | 'multiple';
export type TableRowKey<T> = (row: T, index: number) => string | number;

export interface ITableSortParams<T> {
  attribute: string;
  order?: TableSortOrder;
  compare: (
    a: T,
    b: T,
    order: TableSortOrder,
    attribute: string,
    lang?: string
  ) => number;
  language?: string;
}

export interface ITableLabels {
  noEntriesFoundMessage: string;
  entriesText: string;
  entriesPerPageText: string;
  ofEntriesText: string;
}

@Component({
  selector: 'aw-table',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppendTestIdPipe,
    DropDownComponent,
    LoadingAnimationComponent,
  ],
  templateUrl: './table.component.html',
})
export class TableComponent<T> implements AfterViewInit, OnChanges, OnDestroy {
  public readonly isLoading: InputSignal<boolean> = input(false);
  public readonly id: InputSignal<string> = input.required();
  public readonly hiddenColumns: InputSignal<string[]> = input<string[]>([]);
  public readonly density: InputSignal<TableDensity> =
    input<TableDensity>('comfortable');
  public readonly rowFlow: InputSignal<TableRowFlow> =
    input<TableRowFlow>('nowrap');
  public readonly tone: InputSignal<TableTone> = input<TableTone>('subtle');
  public readonly showRowDividers: InputSignal<boolean> = input<boolean>(true);
  public readonly labels: InputSignal<ITableLabels> = input({
    noEntriesFoundMessage: 'Keine Daten gefunden',
    ofEntriesText: 'von',
    entriesText: 'Einträge',
    entriesPerPageText: 'Ergebnisse pro Seite:',
  });
  public readonly pageSizeOptions: InputSignal<number[]> = input<number[]>([
    5, 10, 15, 20, 50,
  ]);
  public readonly defaultPageSize: InputSignal<number> = input<number>(10);
  public readonly data: InputSignal<T[] | undefined> = input();
  public readonly showNumberOfEntries: InputSignal<boolean> = input(true);
  public readonly showPageSizeSelect: InputSignal<boolean> = input(true);
  public readonly showHeader: InputSignal<boolean> = input(false);
  public readonly striped: InputSignal<boolean> = input<boolean>(false);
  public readonly header: InputSignal<TemplateRef<unknown>> =
    input.required<TemplateRef<unknown>>();
  public readonly expansionMode: InputSignal<TableExpansionMode> =
    input<TableExpansionMode>('single');
  public readonly rowKey: InputSignal<TableRowKey<T> | undefined> = input<
    TableRowKey<T> | undefined
  >(undefined);
  public readonly expandable: InputSignal<boolean> = input<boolean>(false);
  public readonly expandedRowTemplate: InputSignal<
    TemplateRef<unknown> | undefined
  > = input<TemplateRef<unknown> | undefined>(undefined);
  public readonly expandedRowKeys: WritableSignal<Set<string | number>> =
    signal(new Set<string | number>());

  public readonly columns: InputSignal<TemplateRef<unknown>> =
    input.required<TemplateRef<unknown>>();

  public readonly pageChange: OutputEmitterRef<number> = output<number>();

  public currentPage: WritableSignal<number> = signal(0);

  public pageSize: Signal<number>;
  public readonly pageSizeControl: FormControl<number> =
    new FormControl<number>(10, {
      nonNullable: true,
    });

  private getRowKey(row: T, index: number): string | number {
    const rowKeyFn = this.rowKey();
    return rowKeyFn ? rowKeyFn(row, index) : index;
  }
  public isExpanded(row: T, index: number): boolean {
    return this.expandedRowKeys().has(this.getRowKey(row, index));
  }
  public toggleExpanded(row: T, index: number): void {
    if (!this.expandable()) {
      return;
    }

    const key = this.getRowKey(row, index);
    const current = this.expandedRowKeys();
    const next = new Set(current);

    if (this.expansionMode() === 'single') {
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.clear();
        next.add(key);
      }
    } else {
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
    }

    this.expandedRowKeys.set(next);
  }

  public collapseAllExpandedRows(): void {
    this.expandedRowKeys.set(new Set<string | number>());
  }

  public readonly currentPageData: Signal<T[] | undefined> = computed(() => {
    return this.calculateCurrentPageData(
      this.pageSize(),
      this.currentPage(),
      this.sortParams()
    );
  });
  public readonly resolvedTableClasses: Signal<string> = computed(() => {
    return 'overflow-x-auto w-full';
  });

  public readonly resolvedHeaderClasses: Signal<string> = computed(() => {
    const densityClass = this.density() === 'compact' ? 'text-sm' : 'text-base';
    const toneClass = this.tone() === 'subtle' ? '' : 'font-semibold';

    return [
      'flex flex-row border-b border-standard-outline-light',
      densityClass,
      toneClass,
    ].join(' ');
  });

  public readonly resolvedRowClasses: Signal<string> = computed(() => {
    const flowClass = this.rowFlow() === 'wrap' ? 'flex-wrap' : 'flex-nowrap';
    const dividerClass = this.showRowDividers()
      ? 'border-b border-standard-outline'
      : '';

    return [
      'flex flex-row w-full py-2 items-center justify-start',
      flowClass,
      dividerClass,
    ]
      .join(' ')
      .trim();
  });

  public readonly maxPage: Signal<number> = computed(() => {
    const rawPages: number = this.data()
      ? this.data()!.length / this.pageSize()
      : 1;
    const modulo: number = this.data()
      ? this.data()!.length % this.pageSize()
      : 0;
    const pages = modulo != 0 ? Math.ceil(rawPages) : Math.floor(rawPages);
    return pages < 1 ? 1 : pages;
  });

  public sortBy: (sortParams: ITableSortParams<T> | undefined) => void = (
    sortParams: ITableSortParams<T> | undefined
  ) => {
    const oldParams: ITableSortParams<T> | undefined = this.sortParams();
    const order: TableSortOrder =
      sortParams?.attribute === oldParams?.attribute
        ? oldParams?.order === ESortOrder.ASC
          ? ESortOrder.DESC
          : oldParams?.order === ESortOrder.DESC
            ? undefined
            : ESortOrder.ASC
        : sortParams?.order;
    this.sortParams.set(sortParams ? { ...sortParams, order } : undefined);
  };

  private sortParams: WritableSignal<ITableSortParams<T> | undefined> =
    signal(undefined);

  private readonly $hiddenColumns: Observable<string[]> = toObservable(
    this.hiddenColumns
  );
  private readonly $handleKeyUpEvent: Subscription;
  private readonly appendTestId: AppendTestIdPipe = new AppendTestIdPipe();
  @ContentChildren(TableHeaderComponent) tableHeaderCells!: QueryList<
    TableHeaderComponent<T>
  >;
  @ContentChildren(TableCellComponent) tableCells!: QueryList<
    TableCellComponent<T>
  >;
  @ViewChild('table') table!: ElementRef;

  private tableObserver: ResizeObserver = new ResizeObserver(() => {
    this.scaleAndHideCells(
      this.hiddenColumns(),
      this.tableCells,
      this.tableHeaderCells
    );
  });

  constructor() {
    this.pageSize = toSignal(
      this.pageSizeControl.valueChanges.pipe(
        map(pageSize => +pageSize),
        map(pageSize => this.resetInvalidPageSize(pageSize)),
        tap(pageSize => this.autoCorrectCurrentPage(pageSize))
      )
    ) as Signal<number>;
    this.pageSizeControl.updateValueAndValidity();

    this.$handleKeyUpEvent = fromEvent<
      KeyboardEvent & { target?: { id: string } }
    >(document, 'keyup').subscribe(event => {
      const target = this.determineTarget(event.target?.id);
      if (event.key === 'Enter') {
        event.preventDefault();
        switch (target) {
          case 'previous':
            this.previousPage();
            break;
          case 'next':
            this.nextPage();
            break;
        }
      }
    });
  }

  ngOnChanges(): void {
    this.autoCorrectCurrentPage(this.pageSize());
  }

  ngAfterViewInit() {
    this.pageSizeControl.setValue(this.defaultPageSize());
    combineLatest([
      this.tableCells.changes,
      this.tableHeaderCells.changes,
      this.$hiddenColumns,
    ]).subscribe(([tableCells, tableHeaderCells, hiddenColumns]) => {
      this.scaleAndHideCells(hiddenColumns, tableCells, tableHeaderCells);
    });
    this.tableObserver.observe(this.table.nativeElement);
    this.scaleAndHideCells(
      this.hiddenColumns(),
      this.tableCells,
      this.tableHeaderCells
    );
  }

  private scaleAndHideCells(
    hiddenColumns: string[],
    cells: QueryList<TableCellComponent<T>>,
    headerCells: QueryList<TableHeaderComponent<T>>
  ): void {
    const columnWidths: { [key: string]: number } = {};
    headerCells.forEach(cell => {
      if (hiddenColumns.includes(cell.cellName())) {
        cell.hideCell();
      } else {
        cell.showCell();
      }
    });
    cells.forEach(cell => {
      if (hiddenColumns.includes(cell.cellName())) {
        cell.hideCell();
      } else {
        cell.showCell();
      }
    });

    headerCells.forEach(cell => {
      if (cell.isCellVisible()) {
        columnWidths[cell.cellName()] =
          cell.element.nativeElement.getBoundingClientRect()!.width;
      }
    });
    cells.forEach(cell => {
      const measuredWidth = columnWidths[cell.cellName()];
      if (!measuredWidth) {
        return;
      }

      const width = measuredWidth + 'px';
      cell.element.nativeElement.style.flex = `0 0 ${width}`;
      cell.element.nativeElement.style.width = width;
      cell.element.nativeElement.style.minWidth = width;
      cell.element.nativeElement.style.maxWidth = width;
    });
  }

  private determineTarget(targetId: string): 'next' | 'previous' | 'invalid' {
    switch (targetId) {
      case this.appendTestId.transform(this.id(), 'nextPage'):
        return 'next';
      case this.appendTestId.transform(this.id(), 'previousPage'):
        return 'previous';
      default:
        return 'invalid';
    }
  }

  private resetInvalidPageSize(pageSize: number): number {
    return pageSize < 0 ? this.pageSize() : pageSize;
  }

  private autoCorrectCurrentPage(pageSize: number) {
    const currentPageData = this.calculateCurrentPageData(
      pageSize,
      this.currentPage(),
      this.sortParams()
    );
    if (
      (!currentPageData || currentPageData!.length === 0) &&
      this.currentPage() > 0
    ) {
      this.setCurrentPage(0);
    }
  }

  public nextPage(): void {
    if (this.currentPage() < this.maxPage() - 1) {
      this.setCurrentPage(this.currentPage() + 1);
    }
  }

  public previousPage(): void {
    if (this.currentPage() > 0) {
      this.setCurrentPage(this.currentPage() - 1);
    }
  }

  public calculateCurrentPageData(
    pageSize: number,
    currentPage: number,
    sortParams: ITableSortParams<T> | undefined = undefined
  ): T[] | undefined {
    const originalData = this.data();
    const data = originalData ? [...originalData] : [];
    return data
      ? data
          .sort((a: T, b: T) => {
            if (sortParams) {
              this.tableHeaderCells.forEach(tableHeader => {
                if (
                  tableHeader.sortParams()?.attribute !== sortParams?.attribute
                ) {
                  tableHeader.resetParam();
                }
              });
            }
            return sortParams?.order
              ? sortParams?.compare(
                  a,
                  b,
                  sortParams.order,
                  sortParams.attribute,
                  sortParams?.language
                )
              : 0;
          })
          .filter((d: T, i: number) => this.isOnPage(i, pageSize, currentPage))
      : data;
  }

  public setCurrentPage(page: number): void {
    this.currentPage.set(page);
    this.pageChange.emit(this.currentPage());
  }

  private isOnPage(
    index: number,
    pageSize: number,
    currentPage: number
  ): boolean {
    const start = pageSize * currentPage;
    const end = start + pageSize;
    return index >= start && index < end;
  }

  ngOnDestroy() {
    this.$handleKeyUpEvent.unsubscribe();
    this.tableObserver.unobserve(this.table.nativeElement);
  }
}
